import { notification } from 'antd';
import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { history } from 'umi';

import { bindServices } from '@/services/api';

const api = bindServices('customListConfig');
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike, isNullOrEmpty, sortFn, toPaginate } from '@/utils/infrastructure';
import { queryExport, runListCommand } from '../service';
import {
  commandByKey,
  fieldAndColumnMapper,
  hydrateEntityFieldDefinitions,
  localCustomListCommands,
} from './utils';

const {
  CustomListType,
  CustomListStringType,
  CustomListFieldStringType,
  CustomListCommandStringType,
  CustomListCommandType,
  CustomListDataOrigin,
  FormFieldStringType,
  FormFieldType,
  CustomListFieldType,
} = CoreEnums;

const handleFieldDisplayText = (item, data = []) => {
  const { field, internalName } = _.head(data) || {};

  if (!internalName) return;
  let val = item[internalName];

  switch (field?.type) {
    case FormFieldStringType.SelectorArea:
    case FormFieldStringType.SelectorPosition:
    case FormFieldStringType.SelectorUser:
    case FormFieldStringType.SelectorUserGroup:
    case FormFieldStringType.DataBaseRemoteList:
    case FormFieldStringType.DataBaseRemoteSearch:
    case FormFieldStringType.WebServiceRemoteList:
    case FormFieldStringType.WebServiceRemoteSearch:
    case FormFieldStringType.FieldDynamicEntityList:
    case FormFieldStringType.FieldDynamicEntitySearch:
    case FormFieldStringType.Choice:
    case FormFieldType.SelectorArea:
    case FormFieldType.SelectorPosition:
    case FormFieldType.SelectorUser:
    case FormFieldType.SelectorUserGroup:
    case FormFieldType.DataBaseRemoteList:
    case FormFieldType.DataBaseRemoteSearch:
    case FormFieldType.WebServiceRemoteList:
    case FormFieldType.WebServiceRemoteSearch:
    case FormFieldType.FieldDynamicEntityList:
    case FormFieldType.FieldDynamicEntitySearch:
    case FormFieldType.Choice: {
      if (isJsonLike(val)) {
        val = JSON.parse(val);
        const { text, displayText } = val?.items?.[0] || val || {};
        return displayText || text;
      }

      return val;
    }

    default:
      return val;
  }
};

const transformCmdKeys = (type, items = [], fields = []) => {
  try {
    return _.map(items, (item) => {
      const isApproveNoListType =
        item?.[`Command_${commandByKey[CustomListCommandType.ApproveRequest]}`] &&
        type !== CustomListType.CardsList &&
        type !== CustomListType.List &&
        type !== CustomListStringType.CardsList &&
        type !== CustomListStringType.List;

      const temp = fields.filter((o) => {
        return ![
          CustomListFieldType.InstanceName,
          CustomListFieldType.InstanceStatus,
          CustomListFieldType.InstancePercentCompleted,
          CustomListFieldStringType.InstanceName,
          CustomListFieldStringType.InstanceStatus,
          CustomListFieldStringType.InstancePercentCompleted,
        ].includes(o.type);
      });

      if (
        (type === CustomListType.CardsList || type === CustomListStringType.CardsList) &&
        fields?.length > 0
      ) {
        const percentage = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.InstancePercentCompleted ||
            fld.type === CustomListFieldStringType.InstancePercentCompleted,
        );

        const statusField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.InstanceStatus ||
            fld.type === CustomListFieldStringType.InstanceStatus,
        );

        const nameField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.InstanceName ||
            fld.type === CustomListFieldStringType.InstanceName,
        );

        // instance-current-action: header step chip only, excluded from body grid.
        const currentActionField = _.find(fields, (fld) => fld.type === 'instance-current-action');

        // Identifier for title fallback and meta line.
        const identifierField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.InstanceIdentifier ||
            fld.type === CustomListFieldStringType.InstanceIdentifier ||
            fld.type === CustomListFieldType.EntityItemIdentifier ||
            fld.type === CustomListFieldStringType.EntityItemIdentifier,
        );

        // Process / group labels for meta line.
        const processNameField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.ProcessName ||
            fld.type === CustomListFieldStringType.ProcessName,
        );
        const processGroupField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.ProcessGroupName ||
            fld.type === CustomListFieldStringType.ProcessGroupName,
        );

        // Applicant + created for footer; first matching applicant field wins.
        const applicantField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.ApplicantFullName ||
            fld.type === CustomListFieldStringType.ApplicantFullName ||
            fld.type === CustomListFieldType.ApplicantNames ||
            fld.type === CustomListFieldStringType.ApplicantNames,
        );

        const createdField = _.find(
          fields,
          (fld) =>
            fld.type === CustomListFieldType.InstanceCreated ||
            fld.type === CustomListFieldStringType.InstanceCreated ||
            fld.type === CustomListFieldType.EntityItemCreated ||
            fld.type === CustomListFieldStringType.EntityItemCreated,
        );

        // Body grid: visible fields minus reserved header/footer slots.
        const reservedSlots = new Set(
          [
            currentActionField?.internalName,
            identifierField?.internalName,
            processNameField?.internalName,
            processGroupField?.internalName,
            applicantField?.internalName,
            createdField?.internalName,
          ].filter(Boolean),
        );

        const bodyFields = temp
          .filter((o) => !reservedSlots.has(o.internalName))
          .map((o) => {
            if (_.isNil(item?.[o.internalName])) return null;

            const value = isJsonLike(item?.[o.internalName])
              ? JSON.parse(item?.[o.internalName])?.displayText
              : item?.[o.internalName];

            return {
              name: o.displayText,
              value,
              internalName: o.internalName,
              // dataOrigin 1 → form-builder field (`field.type`).
              // dataOrigin 2 → dynamic entity field (`dynamicEntityField.type`).
              // Both branches resolve to the same vocabulary the icon
              // resolver expects (text, date, selector-user, …).
              fieldType: o.dynamicEntityField?.type || o.field?.type,
              cellType: o.type,
              format: o.dynamicEntityField?.format || o.field?.format,
            };
          })
          .filter(Boolean);

        Object.assign(item, {
          fieldInstanceStatus: statusField?.internalName,
          fieldInstanceName: nameField?.internalName,
          fieldCurrentAction: currentActionField?.internalName,
          fieldIdentifier: identifierField?.internalName,
          fieldProcessName: processNameField?.internalName,
          fieldProcessGroup: processGroupField?.internalName,
          fieldApplicant: applicantField?.internalName,
          fieldCreated: createdField?.internalName,
          fieldDisplayText: handleFieldDisplayText(item, temp),
          instancePercentCompleted: percentage?.internalName,
          fields: bodyFields,
        });
      }

      return {
        ...item,
        isCustomListCard:
          type === CustomListType.CardsList || type === CustomListStringType.CardsList,
        ...(isApproveNoListType && {
          [`Command_${commandByKey[CustomListCommandType.RejectRequest]}`]: 1,
        }),
      };
    });
  } catch (err) {
    console.error('ERR_CUSTOM_LIST_TRANSF', err?.message || err);
  }
};

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const notify = {
  edited: false,
  created: false,
  removed: false,
};

const initialState = {
  data: [],
  customListConfig: null,
  filtersConfig: [],
  pagination: meta,
  searchCriteria: null,
  ...notify,
  addFormVisible: false,
  dynamicEntityId: null,
  listId: null,
  currentItem: {},
  orderBy: '',
  bulkCommand: null,
  readPriority: false,
};

const Model = {
  namespace: 'customListConfig',
  state: initialState,
  effects: {
    *fetchConfig({ payload }, { call, put }) {
      try {
        const config = yield call(api.getById, {
          route: 'list/getConfig',
          id: payload.id,
          params: payload.params || {},
        });

        let canManage = false;
        try {
          // `list/manage` (access-aware) es la fuente de verdad para
          // el flag `canManage` por registro — admins y colaboradores
          // reciben `true`, el resto `false` — alineado con el resto
          // del dominio.
          const manage = yield call(api.getAll, {
            route: 'list/manage',
            filter: `id eq ${payload.id}`,
            select: 'id,canManage',
            top: 1,
          });
          canManage = manage?.items?.[0]?.canManage === true;
        } catch (_manageErr) {
          canManage = false;
        }

        yield put({
          type: 'queryConfig',
          payload: { ...payload, config, canManage },
        });
      } catch (e) {
        // 301: list belongs to another tenant; global handler shows switch toast; keep URL.
        if (e?.status === 301) return;

        // Other errors: go to lists index or embed redirect.
        const route = payload.embedConfig ? payload.embedConfig.redirectRoute : '/custom-lists';
        history.replace(route);
      }
    },

    *fetchData({ payload }, { call, put, select }) {
      try {
        const { readPriority, byFields } = yield select((state) => {
          return {
            readPriority: state.customListConfig?.readPriority,
            byFields: state.customListConfig?.groupByFields?.length > 0,
          };
        });

        const res = yield call(api.getById, {
          route: 'list/getData',
          method: 'post',
          readPriority,
          ...payload,
          ...(byFields && { limit: 100_000 }),
        });

        yield put({
          type: byFields ? 'queryByFieldData' : 'queryData',
          payload: {
            ...res,
            ..._.pick(payload, ['limit', 'page']),
          },
        });
      } catch (e) {}
    },

    *fetchById({ payload: id }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: 'customList',
          id,
          params: { select: 'id,name' },
        });
        yield put({ type: 'queryById', payload: res });
      } catch (e) {}
    },

    *editBy({ payload }, { call, put }) {
      try {
        yield call(api.update, {
          route: `list/${payload.listId}/${payload.itemId}`,
          method: 'patch',
          data: payload.data || {},
        });
        yield put({ type: 'queryEdit', payload: true });
      } catch (e) {}
    },

    *create({ payload }, { call, put }) {
      try {
        yield call(api.create, {
          route: `list/${payload.listId}`,
          data: payload.data,
        });
        yield put({ type: 'queryCreate', payload: true });
      } catch (e) {}
    },

    *remove({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `list/${payload.listId}/${payload.itemId}`,
          method: 'delete',
        });
        yield put({ type: 'queryRemove', payload: true });
      } catch (e) {}
    },

    *runCommand({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/isCmdRunning',
          payload: _.pick(payload, ['command', 'rowIndex']),
        });
        yield call(runListCommand, payload);
        yield put({ type: 'queryRunCommand', payload });
      } catch (e) {
      } finally {
        yield put({ type: 'global/isCmdRunning' });
      }
    },

    // Dynamic entity import (same flow as manageEntity/import, kept in this namespace).
    *entityImport({ payload }, { call, put }) {
      try {
        const res = yield call(api.apiRoute, {
          method: 'post',
          route: `entity/import/${payload.id}`,
          data: payload.data,
        });

        if (res?.message) {
          notification.success({
            description: res.message,
            message: '',
            duration: 15,
          });
        }

        // executedCommand triggers list/board refresh in byList/index.jsx.
        yield put({
          type: 'queryRunCommand',
          payload: { command: CustomListCommandStringType.EntityImport },
        });
      } catch (e) {}
    },

    *bulkCommand({ payload, callback }, { call, put }) {
      try {
        yield call(api.bulkCommand, payload, callback);
        yield put({ type: 'global/ongoingOps' });
        yield put({ type: 'queryBulkCommand', payload });
      } catch (e) {
        yield put({ type: 'global/ongoingOps' });
        yield put({
          type: 'queryBulkCommand',
          payload: { command: -1 },
        });
      }
    },

    *export({ payload }, { call }) {
      try {
        const res = yield call(queryExport, payload);
        if (res?.message) {
          const [message, description] = _.split(res.message, '.');
          notification.destroy();
          notification.success({
            ...(message && { message }),
            ...(description && { description }),
            ...(!isMobileOnly && { className: 'ace-export-notice' }),
            style: { minWidth: 600 },
            duration: 10,
          });
        } else {
          const pre = `data:${res.contentType};base64,${res.content}`;
          const a = document.createElement('a');
          a.setAttribute('download', res.fileName);
          a.setAttribute('href', pre);
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          a?.remove();
        }
      } catch (e) {}
    },

    *activeGroupByField({ payload }, { put }) {
      yield put({ type: 'queryGroupByField', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *paginate({ payload }, { put }) {
      yield put({ type: 'queryPage', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *toggleAddForm({ payload }, { put }) {
      yield put({ type: 'queryFormVisibility', payload });
    },

    // Opens the AddForm drawer in "edit" mode for popup-form lists
    // (`formMode: "popup"`). Stores the record under `currentItem`
    // so the drawer can pre-fill values and switch its submit handler
    // from `create` to `editBy`. Closing the drawer (toggleAddForm
    // with `false`) clears `currentItem` automatically.
    *openEditPopup({ payload }, { put }) {
      yield put({
        type: 'queryFormVisibility',
        payload: { visible: true, item: payload },
      });
    },

    *cleanup(__, { put }) {
      yield put({ type: 'queryCleanup' });
    },
  },
  reducers: {
    queryConfig(state, { payload }) {
      const {
        fields: rawFields = [],
        commands = [],
        requestCommands = [],
        type,
        name,
        dataOrigin,
        mapListLegends,
        ...config
      } = payload.config;

      // Hydrate every `dynamicEntityField` with its full definition
      // from `entity.fields[]` so inline edit, validation rules and
      // file widgets (allowMultiple, columnWidth…) work correctly.
      // Also stamps the owning `entityId` so selector fields know
      // which entity to query against (`field/getData` requires it).
      // Process-field lists (dataOrigin 1) skip the merge naturally
      // because `entity.fields` will be undefined.
      const fields = hydrateEntityFieldDefinitions(
        rawFields,
        config.entity?.fields,
        config.dynamicEntityId,
      );

      const groupedByFields = config.groupByFields?.length > 0;
      const listId = isNullOrEmpty(payload?.embedConfig) ? null : payload.id;
      const isOriginDynamicEntity = dataOrigin === CustomListDataOrigin.DynamicEntity;
      const { formFields, filtersConfig, columns, withEditableFields } = fieldAndColumnMapper(
        fields,
        listId,
      );

      return {
        ...state,
        ...notify,
        listId: payload.id,
        executedCommand: null,
        filtersConfig,
        type,
        ...config,
        data: [],
        customListConfig: {
          name,
          type,
          canManage: payload.canManage === true,
          columns,
          mapListLegends,
          fields: fields.sort(sortFn),
          formFields,
          dataOrigin,
          // Edit-mode flag (`"inline"` | `"popup"`). Surfaced on the
          // sub-object so consumers spreading `{...customListConfig}`
          // into ListView/CardsView/etc. receive it as a prop. The
          // top-level spread above (`...config`) keeps it on the
          // namespace too for backwards compatibility.
          formMode: config.formMode,
          groupedByFields,
          ...(requestCommands.length > 0 && { requestCommands }),
          commands: [
            ...commands,
            ...(type === CustomListType.List ||
            type === CustomListType.CardsList ||
            type === CustomListStringType.List ||
            type === CustomListStringType.CardsList
              ? []
              : localCustomListCommands),
          ].filter(Boolean),
          ...(isOriginDynamicEntity && {
            shouldAdd: _.some(
              commands,
              (c) =>
                c.command === CustomListCommandType.EntityAdd ||
                c.command === CustomListCommandStringType.EntityAdd,
            ),
            ...((type !== CustomListType.CardsList || type !== CustomListStringType.CardsList) && {
              shouldDelete: _.some(
                commands,
                (c) =>
                  c.command === CustomListCommandType.EntityDelete ||
                  c.command === CustomListCommandStringType.EntityDelete,
              ),
              shouldEdit:
                _.some(
                  commands,
                  (c) =>
                    c.command === CustomListCommandType.EntityEdit ||
                    c.command === CustomListCommandStringType.EntityEdit,
                ) && withEditableFields,
            }),
          }),
        },
      };
    },

    queryData(state, { payload }) {
      const {
        type,
        customListConfig: { fields = [] },
      } = state;

      return {
        ...state,
        ...notify,
        readPriority: false,
        data: transformCmdKeys(type, payload.items, fields),
        executedCommand: null,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryByFieldData(state, { payload }) {
      const {
        type,
        groupByFields = [],
        groupByPercents = [],
        customListConfig: { fields = [] },
      } = state;

      const data = [];
      const keys = [];
      const result = {};

      for (const item of groupByFields) {
        const field = _.find(fields, { id: item.customListFieldId });
        if (field) {
          const grouped = _.groupBy(payload.items, field.internalName);
          const transformed = Object.entries(grouped).reduce((lhs, [key, items]) => {
            const ordered = _.orderBy(
              items,
              ['ProcessInstance_Status', 'created'],
              ['asc', 'desc'],
            );

            return {
              ...lhs,
              [key]: {
                items: transformCmdKeys(type, ordered, fields),
                groupByField: field.internalName,
                meta: toPaginate({ ...meta, totalCount: items.length }),
              },
            };
          }, {});

          Object.assign(result, transformed);
          keys.push(...Object.keys(transformed));
        }
      }

      for (const key of keys) {
        const { items = [], groupByField } = result[key] || [];

        const filtered = _.filter(items, (item) => {
          return _.some(groupByPercents, (group) => {
            const field = _.find(fields, { id: group.customListFieldId });
            return field ? item[field.internalName] == group.filterValue : false;
          });
        });

        data.push({
          [groupByField]: key,
          count: items.length || 0,
          percent: Math.round(Math.max(filtered.length / items.length, 0) * 100),
          groupByField,
        });
      }

      return {
        ...state,
        ...notify,
        data,
        executedCommand: null,
        readPriority: false,
        allGroupedFieldsData: result,
        pagination: {
          ...state.pagination,
          ...toPaginate({ ...payload, totalCount: data.length }),
        },
        activeGroupByField: localStorage.getItem('x-temp-activeGroupByField')
          ? JSON.parse(localStorage.getItem('x-temp-activeGroupByField'))
          : null,
      };
    },

    queryGroupByField(state, { payload }) {
      return {
        ...state,
        activeGroupByField: payload,
      };
    },

    queryById(state, { payload }) {
      return {
        ...state,
        currentItem: payload,
      };
    },

    queryEdit(state, { payload }) {
      return {
        ...state,
        readPriority: true,
        edited: payload,
      };
    },

    queryCreate(state, { payload }) {
      return {
        ...state,
        created: payload,
        addFormVisible: false,
        readPriority: true,
      };
    },

    queryRemove(state, { payload }) {
      return {
        ...state,
        removed: payload,
        readPriority: true,
      };
    },

    queryRunCommand(state, { payload }) {
      return {
        ...state,
        executedCommand: payload.command,
        executedCommandIsRow: true,
        readPriority: true,
      };
    },

    queryBulkCommand(state, { payload }) {
      return {
        ...state,
        executedCommand: payload.command,
        executedCommandIsRow: false,
        readPriority: true,
      };
    },

    queryFormVisibility(state, { payload }) {
      // Backwards-compatible payloads:
      //   - boolean / null  → just toggle visibility (legacy "add" flow)
      //   - { visible, item } → toggle visibility AND seed `currentItem`
      //                         (used by the popup-edit flow so the
      //                         AddForm drawer can pre-fill values).
      // Closing the drawer (visible falsy) always clears `currentItem`
      // so the next "add" click doesn't reopen with stale data.
      if (_.isPlainObject(payload)) {
        return {
          ...state,
          addFormVisible: !!payload.visible,
          currentItem: payload.visible ? payload.item ?? {} : {},
        };
      }
      const visible = payload ?? false;
      return {
        ...state,
        addFormVisible: visible,
        ...(!visible && { currentItem: {} }),
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryCleanup() {
      return initialState;
    },
  },
};

export default Model;
